import React from 'react';
import tw from 'tailwind-styled-components';
import {FaArrowLeft, FaBoxOpen, FaCheckCircle, FaClock, FaExclamationCircle, FaTimesCircle, FaChevronRight, FaReceipt, FaImage, FaShoppingBag, FaCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import useReturns from '../hooks/useReturns';
import { useSelector } from 'react-redux';
import { ReturnsRefundsLoading } from '../components/SkeletonLoading/ReturnsRefundsLoading';

const getStatusConfig = (status) => {
    const returnStatus = status?.toLowerCase();
    switch (returnStatus) {
        case 'approved':
            return {
                color: 'text-green-600 bg-green-100',
                icon: FaCheckCircle,
                label: 'Approved'
            };
        case 'rejected':
            return {
                color: 'text-red-600 bg-red-100',
                icon: FaTimesCircle,
                label: 'Rejected'
            };
        case 'completed':
            return {
                color: 'text-green-600 bg-green-100',
                icon: FaReceipt,
                label: 'Refunded'
            };
        case 'pending':
        default:
            return {
                color: 'text-yellow-700 bg-yellow-100',
                icon: FaClock,
                label: 'Pending'
            };
    }
};

const ReturnsRefunds = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const { data: returns, isLoading, isError, refetch } = useReturns();

    const handleViewOrder = () => {
        navigate('/orders');
    };

    const returnsHeader = () => (
        <Header>
            <div className="flex items-center space-x-3">
                <BackButton onClick={() => navigate(-1)}>
                    <FaArrowLeft className="w-5 h-5" />
                </BackButton>
                <div className="flex items-center space-x-2">
                    <PageTitle>Returns & Refunds</PageTitle>
                </div>
            </div>
            {returns && returns.length > 0 && (
                <div className="flex justify-between items-center">
                    <div className="text-lg font-semibold text-kuality-blue">
                        {returns.length} {returns.length === 1 ? 'return' : 'returns'}
                    </div>
                </div>
            )}
        </Header>
    );


    if (isLoading) {
        return (
            <PageContainer>
                {returnsHeader()}
                <ReturnsRefundsLoading />
            </PageContainer>
        );
    }

    if (!user) {
        return (
            <PageContainer>
                {returnsHeader()}
                <div className="flex-1 flex flex-col items-center justify-center pb-12">
                    <FaExclamationCircle className="text-6xl text-gray-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-500 mb-2">Please Login or Register</h2>
                    <p className="text-gray-500 px-2 text-center">You need to be logged in to view your return history and status.</p>
                </div>
            </PageContainer>
        );
    }

    if (isError) {
        return (
            <PageContainer>
                {returnsHeader()}
                <ErrorContainer>
                    <ErrorTitle>Unable to Load Returns</ErrorTitle>
                    <ErrorMessage>Something went wrong while fetching your return requests.</ErrorMessage>
                    <RetryButton onClick={() => refetch()}>Try Again</RetryButton>
                </ErrorContainer>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {returnsHeader()}
            <ContentArea>
                {!returns || returns.length === 0 ? (
                    <EmptyState>
                        <EmptyStateIcon className="text-6xl text-gray-500 mb-4" />
                        <EmptyStateTitle>No Returns Yet</EmptyStateTitle>
                        <EmptyStateMessage>You haven't requested any returns or refunds yet.</EmptyStateMessage>
                        <ActionBtn onClick={() => navigate('/orders')}>Go to My Orders</ActionBtn>
                    </EmptyState>
                ) : (
                    <ReturnsList>
                        {returns.map((returnItem) => {
                            const statusConfig = getStatusConfig(returnItem.status);
                            const StatusIcon = statusConfig.icon;

                            const displayItems = returnItem.items && returnItem.items.length > 0
                                ? returnItem.items
                                : [returnItem.product].filter(Boolean);

                            const firstItem = displayItems[0];
                            const firstItemImage = firstItem?.images?.[0]?.image_url || firstItem?.image_url || firstItem?.image;

                            return (
                                <ReturnCard key={returnItem._id || returnItem.id}>
                                    <CardHeader>
                                        <HeaderContent>
                                            <IconAndInfo>
                                                <ImageContainer>
                                                    {firstItemImage ? (
                                                        <CardImage
                                                            src={firstItemImage}
                                                            alt={firstItem.name || 'Product'}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <ImagePlaceholder
                                                        style={{ display: firstItemImage ? 'none' : 'flex' }}
                                                    >
                                                        <FaShoppingBag className="text-kuality-blue" />
                                                    </ImagePlaceholder>
                                                </ImageContainer>
                                                <InfoColumn>
                                                    <OrderId>Order ID: {returnItem.order_id}</OrderId>
                                                    <DateText>
                                                        Requested on {moment(returnItem.createdAt).format('MMM DD, YYYY')}
                                                    </DateText>
                                                </InfoColumn>
                                            </IconAndInfo>
                                            <StatusBadge className={statusConfig.color}>
                                                <StatusIcon className="text-sm" />
                                                <span className="text-sm font-medium">{statusConfig.label}</span>
                                            </StatusBadge>
                                        </HeaderContent>
                                    </CardHeader>

                                    <CardBody>
                                        {displayItems.map((item, idx) => (
                                            <ProductRow key={idx}>
                                                <ProductDetails>
                                                    <ProductName>{item.name || item.product_name}</ProductName>
                                                    <ProductMeta>
                                                        <span>Qty: {item.quantity || 1}</span>
                                                        <span className="mx-2">
                                                            <FaCircle className='w-1 h-1' />
                                                        </span>
                                                        <span className="font-medium text-gray-600">{returnItem.reason || 'N/A'}</span>
                                                    </ProductMeta>

                                                    {returnItem.description && (
                                                        <ReturnComment>
                                                            <span className="font-semibold text-gray-700">Description: </span>
                                                            <p>{returnItem.description}</p>
                                                        </ReturnComment>
                                                    )}
                                                    
                                                    {returnItem.images && returnItem.images.length > 0 && (
                                                        <ImageSection>
                                                            <ImageLabel>
                                                                <FaImage className="mr-1.5 text-gray-400" />
                                                                Attachments
                                                            </ImageLabel>
                                                            <ImageGrid>
                                                                {returnItem.images.map((img, i) => (
                                                                    <ThumbnailImage
                                                                        key={i}
                                                                        src={img}
                                                                        alt={`${i + 1}`}
                                                                        onClick={() => window.open(img, '_blank')}
                                                                    />
                                                                ))}
                                                            </ImageGrid>
                                                        </ImageSection>
                                                    )}
                                                </ProductDetails>
                                            </ProductRow>
                                        ))}
                                    </CardBody>

                                    <CardFooter>
                                        <FooterContent>
                                            <RefundInfo>
                                                <span className="text-sm text-gray-500 font-medium">Refund Amount</span>
                                                <span className="text-md font-semibold text-gray-800">
                                                    ₹{parseFloat(returnItem.refundAmount || returnItem.amount || '0').toFixed(2)}
                                                </span>
                                            </RefundInfo>
                                            <ViewOrderBtn onClick={() => handleViewOrder(returnItem.order_id)}>
                                                <span>View Order</span>
                                                <FaChevronRight className="w-3 h-3 ml-1" />
                                            </ViewOrderBtn>
                                        </FooterContent>
                                    </CardFooter>
                                </ReturnCard>
                            );
                        })}
                    </ReturnsList>
                )}
            </ContentArea>
        </PageContainer>
    );
};

// Styled Components
const PageContainer = tw.div`min-h-screen w-full bg-white flex flex-col`;
const Header = tw.div`flex items-center justify-between p-3 md:p-2 bg-white border-b border-gray-200 sticky top-0 z-10`;
const PageTitle = tw.h1`text-lg font-semibold text-gray-900 tracking-wider md:text-xl`;
const BackButton = tw.button`text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors`;
const ContentArea = tw.div`space-y-4 md:px-16 md:py-8 p-5 `;
const ReturnsList = tw.div`space-y-4`;
const ReturnCard = tw.div`bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200
    hover:border-kuality-blue`;
const CardHeader = tw.div`p-4 border-b border-gray-100`;
const HeaderContent = tw.div`flex items-center justify-between`;
const IconAndInfo = tw.div`flex items-center space-x-3`;
const ImageContainer = tw.div`relative w-12 h-12 rounded-lg overflow-hidden`;
const CardImage = tw.img`w-full h-full object-cover`;
const ImagePlaceholder = tw.div`w-full h-full bg-gray-100 flex items-center justify-center`;
const InfoColumn = tw.div`flex flex-col`;
const OrderId = tw.p`font-semibold text-gray-800`;
const DateText = tw.p`text-sm text-gray-600`;
const StatusBadge = tw.div`flex items-center space-x-2 px-3 py-1 rounded-full`;
const CardBody = tw.div`p-4 flex flex-col gap-4`;
const ProductRow = tw.div`flex gap-4 items-start`;
const ProductDetails = tw.div`flex flex-col flex-1 min-w-0`;
const ProductName = tw.h3`text-sm md:text-base font-semibold text-gray-800 line-clamp-2 leading-tight mb-1`;
const ProductMeta = tw.div`flex items-center text-xs text-gray-500 mb-3`;
const ReturnComment = tw.div`text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100 mb-3`;
const ImageSection = tw.div`mt-1`;
const ImageLabel = tw.span`text-xs text-gray-500 font-semibold mb-2 flex items-center`;
const ImageGrid = tw.div`flex gap-2 overflow-x-auto pb-2 scrollbar-hide`;
const ThumbnailImage = tw.img`w-12 h-12 object-cover rounded-md border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity bg-gray-100 flex-shrink-0`;
const CardFooter = tw.div`p-4 bg-gray-50 border-t border-gray-100`;
const FooterContent = tw.div`flex justify-between items-center`;
const RefundInfo = tw.div`flex flex-col`;
const ViewOrderBtn = tw.button`flex items-center px-3 py-2 text-kuality-blue text-sm font-medium rounded-lg hover:bg-kuality-blue hover:text-white transition-colors border border-kuality-blue hover:border-kuality-blue`;
const EmptyState = tw.div`flex-1 flex flex-col items-center justify-center text-center pb-12 py-10`;
const EmptyStateIcon = tw(FaBoxOpen)``;
const EmptyStateTitle = tw.h3`text-xl font-semibold text-gray-600 mb-2`;
const EmptyStateMessage = tw.p`text-gray-500 mb-6`;
const ActionBtn = tw.button`bg-kuality-blue text-white px-6 py-3 rounded-lg hover:bg-kuality-blue transition-colors`;
const ErrorContainer = tw.div`flex-1 flex flex-col items-center justify-center text-center pb-12 py-10`;
const ErrorTitle = tw.h3`text-lg font-semibold text-gray-800 mb-2`;
const ErrorMessage = tw.p`text-gray-600 mb-4`;
const RetryButton = tw.button`bg-kuality-blue text-white px-4 py-2 rounded-lg hover:bg-kuality-blue transition-colors`;

export default ReturnsRefunds;